/**
 * Custom gift management API with its rules deriving from the `gifts-with-purchase` section.
 */
class GiftManager {
  /**
   * Constructs a new instance of the promotional offer manager.
   * @param {Array<Object>} promos - An array of promotional offers to initialize the manager with.
   * @throws {Error} Throws an error if any of the promotional offers are invalid.
   */
  constructor(promos) {
    // Deep copy promos just as an added layer of obfuscation.
    this.promos = [];
    promos.forEach((promo) => {
      // Verify the promo has all of its required settings assigned and verified.
      if (!promo.id) {
        throw new Error('Invalid promo: configuration requires an identifier.');
      }
      if (!Number.isInteger(promo.giftVariantId)) {
        throw new Error(
          `Invalid promo: ${promo.id} requires a gift variant identifier number.`,
        );
      }
      if (!Number.isInteger(promo.giftQuantity)) {
        throw new Error(
          `Invalid promo: ${promo.id} requires a valid gift quantity number.`,
        );
      }
      if (!Number.isInteger(promo.minSubtotal)) {
        throw new Error(
          `Invalid promo: ${promo.id} requires a minimum subtotal number.`,
        );
      }

      // The array for required products (IDs) for BXGY is optional.
      this.promos.push({ ...promo });
    });
  }

  /**
   * Silently runs the GWP (Gift With Purchase) logic. Invalid gifts are automatically removed from the cart.
   * When done a custom `gift-manager:update` event is dispatched, detailing all eligible gifts and all
   * currently claimed gifts.
   * @param {Object} cart - The Shopify cart object.
   * @returns {Promise<Object>} A Promise that resolves with the updated cart after applying GWP logic.
   */
  run(cart) {
    // Remove all GWPs from the cart first, cannot assume the cart hasn't been tampered with.
    const purgeGifts = cart.items
      .filter((item) => {
        if (item.discounts.length && item.properties.Gift) {
          // Some gifts may be discounted to $0 and will need to be evaluated.
          return true;
        }
        // Evaluate any item that is normally free, is not part of any bundle group,
        // and is not a loyalty redemption.
        return (
          item.price === 0 &&
          !item.properties._group &&
          !item.properties._bundle_id &&
          !item.properties._swell_redemption_token &&
          !item.properties._rc_bundle
        );
      })
      .filter((item) => {
        const promo = this.promos.find((promo) => {
          if (!item.properties || !item.properties._promo_id) {
            return false;
          }
          return promo.id === item.properties._promo_id;
        });
        if (!promo) {
          // There is no promo associated with this gift, so do not filter it out.
          return true;
        }
        // Returns true if this gift is not eligible for the current cart.
        return !this.promoIsValid(cart, promo);
      })
      .reduce((updates, item) => {
        updates[item.key] = 0;
        return updates;
      }, {});

    return window.cart.update(purgeGifts, true).then((cart) => {
      const eligiblePromos = this.promos.filter((promo) =>
        this.promoIsValid(cart, promo),
      );
      document.dispatchEvent(
        new CustomEvent('gift-manager:update', {
          detail: { cart, eligiblePromos },
        }),
      );
    });
  }

  /**
   * Checks if a promotional offer is valid based on the contents of the cart.
   * @param {Object} cart - The Shopify cart object.
   * @param {Object} promo - The promotional offer object to be validated.
   * @returns {boolean} True if the promotional offer is valid; otherwise, false.
   */
  promoIsValid(cart, promo) {
    // Check for product-specific quantity limits.
    if (promo.productId) {
      const totalQuantity = cart.items.reduce((sum, item) => {
        if (item.product_id === promo.productId) {
          return sum + item.quantity;
        }
        return sum;
      }, 0);
      if (totalQuantity > promo.giftQuantity) {
        return false;
      }
    }

    // Check if the cart's subtotal qualifies.
    if (promo.minSubtotal > cart.total_price) {
      return false;
    }

    // Check if this promo requires any other paid product in cart (BXGY).
    if (promo.requiredPurchase?.length) {
      for (let i = 0; i < cart.items.length; i++) {
        const cartItem = cart.items[i];
        if (promo.requiredPurchase.indexOf(cartItem.product_id) > -1) {
          return true;
        }
      }
      // Required purchase not found.
      return false;
    }

    return true;
  }
}

window.GiftManager = GiftManager;
